import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEditorStore } from '../../stores/editorStore';
import { useSettingsStore } from '../../stores/settingsStore';
import SettingsPanel from '../Settings/SettingsPanel';
import DocumentManager from './DocumentManager';
import { saveTextFile, openTextFile } from '../../utils/fileSystem';
import {
  MoonIcon,
  SunIcon,
  CogIcon,
  DocumentPlusIcon,
  FolderOpenIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  QueueListIcon
} from '@heroicons/react/24/outline';

const Toolbar: React.FC = () => {
  const { createDocument, activeDocument, updateDocument } = useEditorStore();
  const { theme, backgroundStyle, updateSettings } = useSettingsStore();
  const [showSettings, setShowSettings] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);

  // Ctrl+B 기능은 App.tsx에서 전역으로 처리됨

  const toggleTheme = () => {
    updateSettings({ theme: theme === 'dark' ? 'light' : 'dark' });
    if (theme === 'dark') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  };

  const handleSave = async () => {
    if (!activeDocument) return;
    await saveTextFile(activeDocument.content, activeDocument.title);
  };

  const handleOpen = async () => {
    const result = await openTextFile();
    
    if (result) {
      createDocument();
      setTimeout(() => {
        const { activeDocument: newDoc, updateDocumentTitle } = useEditorStore.getState();
        if (newDoc) {
          updateDocument(newDoc.id, result.content);
          updateDocumentTitle(newDoc.id, result.name);
        }
      }, 100);
    }
  };

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-40 glass-effect"
        >
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setShowDocuments(true)}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Document Manager"
                >
                  <QueueListIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={createDocument}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="New Document"
                >
                  <DocumentPlusIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleOpen}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Open File"
                >
                  <FolderOpenIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleSave}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Save As"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Toggle Theme"
                >
                  {theme === 'dark' ? (
                    <SunIcon className="w-5 h-5" />
                  ) : (
                    <MoonIcon className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => {
                    const styles = ['solid', 'gradient', 'image'];
                    const currentIndex = styles.indexOf(backgroundStyle);
                    const nextIndex = (currentIndex + 1) % styles.length;
                    updateSettings({ backgroundStyle: styles[nextIndex] as any });
                  }}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Background Style (Ctrl+B)"
                >
                  <PhotoIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Settings"
                >
                  <CogIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      
      <SettingsPanel isOpen={showSettings} onClose={() => setShowSettings(false)} />
      <DocumentManager isOpen={showDocuments} onClose={() => setShowDocuments(false)} />
    </>
  );
};

export default Toolbar;